import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { logger } from '@/lib/logger';
import { useFileOpenStore } from '@/stores/fileOpenStore';
import { useFileIndexStore } from '@/stores/fileIndexStore';
import { useFileTreeStore } from '@/stores/fileTreeStore';
import { useProjectStore } from '@/stores/projectStore';
import { showToast } from '@/components/ui/Toast';
import { useActiveIconPack } from '@/lib/iconPacks/registry';
import { useOpenFilesStore, type OpenFile } from '@/stores/openFilesStore';
import {
  cancelEditorStateWrites,
  loadEditorStateForProject,
  scheduleEditorStateWrite,
} from '@/lib/editorStatePersistence';
import EditorTabs from './EditorTabs';
import MermaidPreview from './MermaidPreview';
import { tsWorkspace } from '@/lib/monacoTSWorkspace';
import { CacheKeys, envelope, readCache, writeCache, type CacheEnvelope } from '@/lib/projectCache';
import { useCacheStatusStore } from '@/stores/cacheStatusStore';
import { useSymbolIndexStore } from '@/lib/symbolIndex';
import { useThemeStore } from '@/stores/themeStore';

const FILE_TREE_CACHE_VERSION = 1;

/** Count total nodes (files + folders) in a possibly-nested tree. Used for perf logs. */
function countTreeNodes(nodes: FileNode[]): number {
  let n = 0;
  for (const node of nodes) {
    n += 1;
    if (node.children) n += countTreeNodes(node.children);
  }
  return n;
}

import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileText,
  FileImage,
  Search,
  Calendar,
  HardDrive,
  AlertCircle,
  X,
  Maximize2,
  ChevronLeft,
  Loader2,
  Check,
  AlertTriangle,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Copy,
  FileText as FileTextIcon,
  FolderSearch,
  Eye,
  Edit3,
  Pencil,
  Trash2,
  FilePlus,
  FolderPlus,
  FileOutput,
  PictureInPicture2,
} from 'lucide-react';
import { getPopoutContext } from '@/lib/popoutContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { useHotkeys } from 'react-hotkeys-hook';
import Editor, { type OnMount } from '@monaco-editor/react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useBackend, isCefHost, CefBackend } from '../../services/backend';
import type { FileNode, GitFileStatus } from '../../services/backend/types';

// (File and folder icons are now resolved through the active IconPack —
//  see src/lib/iconPacks/registry.ts and useActiveIconPack().)

// Get Monaco language based on file extension
const getMonacoLanguage = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'ts':
      return 'typescript';
    case 'tsx':
      return 'typescript';
    case 'js':
      return 'javascript';
    case 'jsx':
      return 'javascript';
    case 'json':
      return 'json';
    case 'html':
      return 'html';
    case 'css':
      return 'css';
    case 'scss':
    case 'sass':
      return 'scss';
    case 'md':
      return 'markdown';
    case 'py':
      return 'python';
    case 'rs':
      return 'rust';
    case 'go':
      return 'go';
    case 'java':
      return 'java';
    case 'cpp':
    case 'cc':
    case 'cxx':
      return 'cpp';
    case 'c':
      return 'c';
    case 'cs':
      return 'csharp';
    case 'php':
      return 'php';
    case 'rb':
      return 'ruby';
    case 'sh':
    case 'bash':
      return 'shell';
    case 'sql':
      return 'sql';
    case 'xml':
      return 'xml';
    case 'yaml':
    case 'yml':
      return 'yaml';
    case 'toml':
      return 'toml';
    default:
      return 'plaintext';
  }
};

// Check if file is a markdown file
const isMarkdownFile = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext === 'md' || ext === 'markdown' || ext === 'mdx';
};

// Check if file is a Mermaid diagram source. Same preview-vs-edit toggle as
// markdown — preview renders the diagram, edit opens Monaco with the source.
const isMermaidFile = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  return ext === 'mmd' || ext === 'mermaid';
};

// Check if file is binary based on extension
const isBinaryFile = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const binaryExtensions = [
    'png', 'jpg', 'jpeg', 'gif', 'bmp', 'ico', 'webp',
    'mp3', 'mp4', 'wav', 'avi', 'mov',
    'zip', 'tar', 'gz', 'rar', '7z',
    'exe', 'dll', 'so', 'dylib',
    'pdf', 'doc', 'docx', 'xls', 'xlsx',
    'woff', 'woff2', 'ttf', 'eot',
  ];
  return ext ? binaryExtensions.includes(ext) : false;
};

// Check if file is a viewable image
const isImageFile = (fileName: string): boolean => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'ico', 'webp', 'svg'];
  return ext ? imageExtensions.includes(ext) : false;
};

// Get MIME type for image files
const getImageMimeType = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'bmp': return 'image/bmp';
    case 'ico': return 'image/x-icon';
    case 'webp': return 'image/webp';
    case 'svg': return 'image/svg+xml';
    default: return 'application/octet-stream';
  }
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date from Unix timestamp
const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000); // Convert from seconds to milliseconds
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const minutes = Math.floor(diff / (1000 * 60));
      return minutes <= 1 ? 'Just now' : `${minutes} minutes ago`;
    }
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;

  return date.toLocaleDateString();
};

// Extended FileNode with content for preview
interface FileNodeWithContent extends FileNode {
  content?: string;
}

// Highlight matched text component
interface HighlightedTextProps {
  text: string;
  query: string;
}

function HighlightedText({ text, query }: HighlightedTextProps) {
  if (!query) {
    return <span>{text}</span>;
  }

  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index} className="bg-warning text-warning-content px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
}

// Git status color mapping
const getGitStatusColor = (status: GitFileStatus['status'] | undefined) => {
  switch (status) {
    case 'modified':
      return 'text-warning';
    case 'added':
    case 'untracked':
      return 'text-success';
    case 'deleted':
      return 'text-error';
    case 'renamed':
    case 'copied':
      return 'text-info';
    case 'conflict':
      return 'text-error font-bold';
    default:
      return '';
  }
};

const getGitStatusIndicator = (status: GitFileStatus['status'] | undefined) => {
  switch (status) {
    case 'modified':
      return 'M';
    case 'added':
      return 'A';
    case 'deleted':
      return 'D';
    case 'renamed':
      return 'R';
    case 'untracked':
      return 'U';
    case 'copied':
      return 'C';
    case 'conflict':
      return '!';
    default:
      return null;
  }
};

// Context menu state
interface ContextMenuState {
  x: number;
  y: number;
  node: FileNodeWithContent;
}

// File tree item component
interface FileTreeItemProps {
  node: FileNodeWithContent;
  level: number;
  selectedId: string | null;
  expandedIds: Set<string>;
  loadingIds: Set<string>;
  flashIds: Set<string>;
  /** Active icon pack — passed in so the memoized item only re-renders on swap. */
  iconPack: import('@/lib/iconPacks/types').IconPack;
  /** ID of the folder currently being drag-hovered (for highlight). */
  dropTargetId: string | null;
  /** Called when an internal drag is dropped onto a folder. */
  onInternalDrop: (sourceId: string, destFolderId: string) => void;
  /** Called when the OS-drag enters a folder; component manages hover-to-expand. */
  onDragEnterFolder: (folderId: string) => void;
  onDragLeaveFolder: (folderId: string) => void;
  gitStatus: Map<string, GitFileStatus['status']>;
  onSelect: (node: FileNodeWithContent) => void;
  onToggle: (id: string, node: FileNodeWithContent) => void;
  onContextMenu: (e: React.MouseEvent, node: FileNodeWithContent) => void;
  searchQuery?: string;
}

const FileTreeItem = memo(function FileTreeItem({
  node,
  level,
  selectedId,
  expandedIds,
  loadingIds,
  flashIds,
  iconPack,
  dropTargetId,
  onInternalDrop,
  onDragEnterFolder,
  onDragLeaveFolder,
  gitStatus,
  onSelect,
  onToggle,
  onContextMenu,
  searchQuery = '',
}: FileTreeItemProps) {
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;
  const isFolder = node.type === 'folder';
  const isLoading = loadingIds.has(node.id);
  const hasChildren = node.hasChildren || (node.children && node.children.length > 0);
  const isGitignored = node.gitignored === true;
  const isFlashing = flashIds.has(node.id);
  const isDropTarget = isFolder && dropTargetId === node.id;

  // Check git status for this file
  const normalizedPath = node.path.replace(/\\/g, '/');
  const fileGitStatus = gitStatus.get(normalizedPath);
  const statusColor = getGitStatusColor(fileGitStatus);
  const statusIndicator = getGitStatusIndicator(fileGitStatus);

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-base-300 rounded ${
          isSelected ? 'bg-primary/20 hover:bg-primary/30' : ''
        } ${isGitignored ? 'opacity-60' : ''} ${isFlashing ? 'animate-flash-amber' : ''} ${
          isDropTarget ? 'ring-2 ring-amber-400/70 bg-amber-500/10' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        // data-folder-id powers OS-drop hit-testing — tauri://drag-drop only
        // gives us a (x, y) position, so we use elementFromPoint + this attr
        // to figure out which folder the user dropped onto.
        data-folder-id={isFolder ? node.id : undefined}
        data-parent-folder-id={isFolder ? undefined : (node.id.includes('/') ? node.id.substring(0, node.id.lastIndexOf('/')) : '')}
        draggable
        onDragStart={(e) => {
          // text/plain stays for drag-out-to-OS UX (copies the absolute path).
          // The custom mime is what we read on internal drop to identify the
          // source — drag events don't expose mime payload during dragover for
          // security reasons, hence the dedicated marker type.
          e.dataTransfer.setData('text/plain', node.path);
          e.dataTransfer.setData('application/x-ateliercode-id', node.id);
          e.dataTransfer.effectAllowed = 'copyMove';
        }}
        onDragEnter={(e) => {
          if (!isFolder) return;
          // Accept either an internal drag or an OS file drag.
          const types = e.dataTransfer.types;
          if (
            !types.includes('application/x-ateliercode-id') &&
            !types.includes('Files')
          ) {
            return;
          }
          e.preventDefault();
          onDragEnterFolder(node.id);
        }}
        onDragOver={(e) => {
          if (!isFolder) return;
          const types = e.dataTransfer.types;
          if (
            !types.includes('application/x-ateliercode-id') &&
            !types.includes('Files')
          ) {
            return;
          }
          e.preventDefault();
          e.dataTransfer.dropEffect = types.includes('application/x-ateliercode-id')
            ? 'move'
            : 'copy';
        }}
        onDragLeave={() => {
          if (!isFolder) return;
          onDragLeaveFolder(node.id);
        }}
        onDrop={(e) => {
          if (!isFolder) return;
          // Internal drop only — OS file drops are intercepted by the
          // tauri://drag-drop event listener which has the actual file paths.
          // The HTML5 drop event for OS files arrives with empty
          // dataTransfer.files in Tauri, so we'd otherwise no-op anyway.
          const sourceId = e.dataTransfer.getData('application/x-ateliercode-id');
          if (!sourceId) {
            onDragLeaveFolder(node.id);
            return;
          }
          e.preventDefault();
          onDragLeaveFolder(node.id);
          onInternalDrop(sourceId, node.id);
        }}
        onClick={() => {
          if (isFolder) {
            onToggle(node.id, node);
          }
          onSelect(node);
        }}
        onContextMenu={(e) => onContextMenu(e, node)}
      >
        {isFolder && hasChildren && (
          <span className="flex-shrink-0">
            {isLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : isExpanded ? (
              <ChevronDown className="w-4 h-4 text-base-content/50" />
            ) : (
              <ChevronRight className="w-4 h-4 text-base-content/50" />
            )}
          </span>
        )}
        {isFolder && !hasChildren && <span className="w-4" />}
        {!isFolder && <span className="w-4" />}

        <span className="flex-shrink-0">
          {isFolder
            ? iconPack.getFolderIcon(node.name, isExpanded)
            : iconPack.getFileIcon(node.name)}
        </span>

        <span className={`text-sm truncate flex-1 ${statusColor}`}>
          <HighlightedText text={node.name} query={searchQuery} />
        </span>

        {/* Git status indicator */}
        {statusIndicator && (
          <span className={`text-xs font-mono ${statusColor} flex-shrink-0`} title={fileGitStatus}>
            {statusIndicator}
          </span>
        )}
        {/* Gitignored indicator (only when no git status badge) */}
        {isGitignored && !statusIndicator && (
          <span className="text-xs font-mono text-base-content/60 flex-shrink-0" title="gitignored">
            I
          </span>
        )}
      </div>

      {isFolder && isExpanded && node.children && (
        <div>
          {node.children.map((child) => (
            <FileTreeItem
              key={child.id}
              node={child}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              loadingIds={loadingIds}
              flashIds={flashIds}
              iconPack={iconPack}
              dropTargetId={dropTargetId}
              onInternalDrop={onInternalDrop}
              onDragEnterFolder={onDragEnterFolder}
              onDragLeaveFolder={onDragLeaveFolder}
              gitStatus={gitStatus}
              onSelect={onSelect}
              onToggle={onToggle}
              onContextMenu={onContextMenu}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// Quick file search component
interface QuickFileSearchProps {
  fileTree: FileNodeWithContent[];
  iconPack: import('@/lib/iconPacks/types').IconPack;
  onSelect: (node: FileNodeWithContent) => void;
  onClose: () => void;
}

function QuickFileSearch({ fileTree, iconPack, onSelect, onClose }: QuickFileSearchProps) {
  const [query, setQuery] = useState('');
  const [filteredFiles, setFilteredFiles] = useState<FileNodeWithContent[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const indexEntries = useFileIndexStore((s) => s.entries);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query) {
      // No query: show files from loaded tree
      const getAllFiles = (nodes: FileNodeWithContent[]): FileNodeWithContent[] => {
        const files: FileNodeWithContent[] = [];
        for (const node of nodes) {
          if (node.type === 'file') files.push(node);
          if (node.children) files.push(...getAllFiles(node.children));
        }
        return files;
      };
      setFilteredFiles(getAllFiles(fileTree).slice(0, 20));
      setSelectedIndex(0);
      return;
    }

    // Use the full index for search when available
    if (indexEntries.length > 0) {
      const results = useFileIndexStore.getState().search(query, 20);
      const asNodes: FileNodeWithContent[] = results
        .filter((e) => e.type === 'file')
        .map((e) => ({
          id: e.path,
          name: e.name,
          path: e.path,
          type: e.type as 'file' | 'folder',
        }));
      setFilteredFiles(asNodes);
    } else {
      // Fallback: search loaded tree
      const getAllFiles = (nodes: FileNodeWithContent[]): FileNodeWithContent[] => {
        const files: FileNodeWithContent[] = [];
        for (const node of nodes) {
          if (node.type === 'file') files.push(node);
          if (node.children) files.push(...getAllFiles(node.children));
        }
        return files;
      };
      const allFiles = getAllFiles(fileTree);
      const filtered = allFiles.filter((file) =>
        file.name.toLowerCase().includes(query.toLowerCase()) ||
        file.path.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredFiles(filtered.slice(0, 20));
    }
    setSelectedIndex(0);
  }, [query, fileTree, indexEntries]);

  const handleSelect = (file: FileNodeWithContent) => {
    onSelect(file);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filteredFiles.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filteredFiles[selectedIndex]) {
      e.preventDefault();
      handleSelect(filteredFiles[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 md:pt-24 px-3 md:px-0 bg-black/50" onClick={onClose}>
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-base-300">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/50" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search files by name or path..."
              className="input input-bordered w-full pl-10 pr-10"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm btn-circle"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {filteredFiles.length === 0 ? (
            <div className="p-8 text-center text-base-content/50">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>No files found</p>
            </div>
          ) : (
            filteredFiles.map((file, index) => (
              <div
                key={file.id}
                className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-base-200 ${
                  index === selectedIndex ? 'bg-primary/20' : ''
                }`}
                onClick={() => handleSelect(file)}
              >
                {iconPack.getFileIcon(file.name)}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{file.name}</div>
                  <div className="text-xs text-base-content/50 truncate">{file.path}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Filter file tree based on search query
const filterFileTree = (
  nodes: FileNodeWithContent[],
  query: string
): { filteredNodes: FileNodeWithContent[]; matchCount: number } => {
  if (!query.trim()) {
    return { filteredNodes: nodes, matchCount: 0 };
  }

  const lowerQuery = query.toLowerCase();
  let totalMatches = 0;

  const filterNode = (node: FileNodeWithContent): FileNodeWithContent | null => {
    const nameMatches = node.name.toLowerCase().includes(lowerQuery);
    const pathMatches = node.path.toLowerCase().includes(lowerQuery);

    if (node.type === 'file') {
      if (nameMatches || pathMatches) {
        totalMatches++;
        return node;
      }
      return null;
    }

    // For folders, check if any children match
    if (node.children) {
      const filteredChildren = node.children
        .map(child => filterNode(child))
        .filter((child): child is FileNodeWithContent => child !== null);

      if (filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren,
        };
      }
    }

    // Include folder if its name matches, even if no children match
    if (nameMatches) {
      totalMatches++;
      return node;
    }

    return null;
  };

  const filteredNodes = nodes
    .map(node => filterNode(node))
    .filter((node): node is FileNodeWithContent => node !== null);

  return { filteredNodes, matchCount: totalMatches };
};

/**
 * Pop-out the Files panel into a secondary OS window. Each Tauri window
 * has its own React tree + zustand stores, so the popout has its own
 * selected file / expansion state / Monaco editor — independent of main.
 *
 * Hidden in popout windows themselves (no nested popouts) and on web.
 */
function FilesPopoutButton({ projectId }: { projectId?: string }) {
  const backend = useBackend();
  const popoutCtx = getPopoutContext();
  const cefMode = isCefHost();
  const canPopout = cefMode && !popoutCtx.isPopout && !!projectId;
  const handleClick = useCallback(async () => {
    if (!canPopout || !projectId) return;
    try {
      if (cefMode && backend instanceof CefBackend) {
        const label = `popout-files-${projectId}`;
        await backend.popout.open(label, 'files', projectId);
      }
    } catch (e) {
      logger.warn('FilesTab', 'popout_open failed', e);
    }
  }, [canPopout, cefMode, backend, projectId]);
  if (!canPopout) return null;
  return (
    <button
      type="button"
      onClick={handleClick}
      className="btn btn-ghost btn-sm btn-square"
      title="Pop out into its own window"
    >
      <PictureInPicture2 className="w-4 h-4" />
    </button>
  );
}

// Main FilesTab component props
interface FilesTabProps {
  projectId?: string;
}

// Main FilesTab component
export default function FilesTab({ projectId }: FilesTabProps) {
  const backend = useBackend();
  // Monaco's syntax theme must follow the app theme — 'vs-dark' colors
  // (light-blue keys, salmon strings) are unreadable on the light editor
  // background, so light mode uses Monaco's built-in 'vs' light theme.
  const appTheme = useThemeStore((s) => s.theme);
  const monacoTheme = appTheme === 'atelier-light' ? 'vs' : 'vs-dark';
  // The tree itself lives in fileTreeStore so the file watcher can apply
  // per-path mutations (insert / remove / flash) without losing the user's
  // expansion state. We expose it through a setState-shaped adapter so the
  // dozens of existing call sites in this component don't need to change.
  const fileTree = useFileTreeStore((s) => s.tree);
  const flashIds = useFileTreeStore((s) => s.flashIds);
  const iconPack = useActiveIconPack();
  const setFileTree = useCallback<React.Dispatch<React.SetStateAction<FileNodeWithContent[]>>>(
    (value) => {
      if (typeof value === 'function') {
        const updater = value as (prev: FileNodeWithContent[]) => FileNodeWithContent[];
        useFileTreeStore.setState((s) => ({ tree: updater(s.tree) }));
      } else {
        useFileTreeStore.getState().setTree(value);
      }
    },
    [],
  );

  const [selectedFile, setSelectedFile] = useState<FileNodeWithContent | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [gitStatus, setGitStatus] = useState<Map<string, GitFileStatus['status']>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving' | 'error'>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string | undefined>(undefined);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [renameTarget, setRenameTarget] = useState<FileNodeWithContent | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [createTarget, setCreateTarget] = useState<{ parent: FileNodeWithContent; type: 'file' | 'folder' } | null>(null);
  const [createValue, setCreateValue] = useState('');
  // Drag-drop state
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  /**
   * Pending external-file drop awaiting user conflict resolution. Set when
   * the OS dropped a file whose name collides with something already in the
   * destination folder; cleared when the modal is dismissed or applied.
   */
  const [conflictPrompt, setConflictPrompt] = useState<{
    sourcePath: string;
    destFolderRel: string;
    fileName: string;
    /** Other queued sources to process after this one resolves (for batch drops). */
    remaining: string[];
  } | null>(null);
  const hoverExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropTargetIdRef = useRef<string | null>(null);
  useEffect(() => { dropTargetIdRef.current = dropTargetId; }, [dropTargetId]);
  /** Ref on the file tree panel — used to ignore OS drops that landed elsewhere. */
  const filesPanelRef = useRef<HTMLDivElement | null>(null);
  /** Queue of remaining external drop sources awaiting a conflict prompt resolution. */
  const dropQueueRef = useRef<Array<{ sourcePath: string; destFolderRel: string }>>([]);
  const [markdownViewMode, setMarkdownViewMode] = useState<'preview' | 'edit'>('preview');
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const handleSelectRef = useRef<((node: FileNodeWithContent) => Promise<void>) | null>(null);
  const fileTreeRef = useRef<FileNodeWithContent[]>([]);

  // Keep fileTreeRef in sync for use in async callbacks
  useEffect(() => { fileTreeRef.current = fileTree; }, [fileTree]);

  // Keyboard shortcuts
  useHotkeys('ctrl+p, cmd+p', (e) => {
    e.preventDefault();
    setShowQuickSearch(true);
  });

  // Keyboard shortcut for inline search (Ctrl/Cmd+F)
  useHotkeys('ctrl+f, cmd+f', (e) => {
    e.preventDefault();
    searchInputRef.current?.focus();
  });

  // Save file function
  const saveFile = async (contentToSave?: string) => {
    if (!projectId || !selectedFile || selectedFile.type !== 'file') return;
    const content = contentToSave ?? editedContent;
    if (content === undefined) return;

    // Cancel pending auto-save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    setSaveStatus('saving');
    setSaveError(null);

    try {
      await backend.files.writeFile(projectId, selectedFile.path, content);
      setSaveStatus('saved');
      // Update the selected file's stored content so switching away won't re-fetch stale data
      setSelectedFile(prev => prev ? { ...prev, content } : prev);
      setEditedContent(undefined);
      // Mirror dirty-state to the tab strip so the indicator clears.
      if (selectedFile) {
        useOpenFilesStore.getState().setDirty(selectedFile.id, false);
      }
    } catch (err) {
      logger.error('FilesTab', 'Failed to save file:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save file';
      setSaveStatus('error');
      setSaveError(msg);
    }
  };

  // Schedule auto-save with debounce
  const scheduleAutoSave = (content: string) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveFile(content);
    }, 1500);
  };

  // Handle editor content changes
  const handleEditorChange = (value: string | undefined) => {
    if (value === undefined) return;
    setEditedContent(value);
    setSaveStatus('unsaved');
    setSaveError(null);
    scheduleAutoSave(value);
    // Tab dot — visible until next save completes.
    if (selectedFile) {
      useOpenFilesStore.getState().setDirty(selectedFile.id, true);
    }
  };

  // Handle editor mount. We capture the monaco instance here (only available
  // post-mount) so we can wire up the TS workspace lazily — before mount the
  // monaco module isn't loaded yet.
  const handleEditorMount: OnMount = (editor, monaco) => {
    logger.info('FilesTab', 'Editor mounted');
    editorRef.current = editor;

    // Throttled scroll capture: store the scrollTop on the active tab so
    // switching projects/files and coming back restores scroll position.
    // Throttle to once per 200ms to avoid flooding the store on every
    // wheel tick.
    let scrollThrottle: ReturnType<typeof setTimeout> | null = null;
    editor.onDidScrollChange(() => {
      if (scrollThrottle) return;
      scrollThrottle = setTimeout(() => {
        scrollThrottle = null;
        const store = useOpenFilesStore.getState();
        const pid = store.currentProjectId;
        const activeId = pid ? store.byProject.get(pid)?.activeId : null;
        if (!activeId) return;
        store.setScrollState(activeId, { scrollTop: editor.getScrollTop() });
      }, 200);
    });

    // Cursor position fires on typing/clicks — much less frequent than
    // scroll, so no throttle needed.
    editor.onDidChangeCursorPosition((e: { position: { lineNumber: number; column: number } }) => {
      const store = useOpenFilesStore.getState();
      const pid = store.currentProjectId;
      const activeId = pid ? store.byProject.get(pid)?.activeId : null;
      if (!activeId) return;
      store.setScrollState(activeId, {
        cursorLine: e.position.lineNumber,
        cursorColumn: e.position.column,
      });
    });

    if (!projectId) return;
    const root = useProjectStore.getState().currentProject?.root_path;
    if (!root) return;
    void tsWorkspace.init(monaco, projectId, root, backend).catch((e) => {
      logger.warn('FilesTab', 'TS workspace init failed:', e);
    });
  };

  // Ctrl+S / Cmd+S manual save
  useHotkeys('ctrl+s, cmd+s', (e) => {
    e.preventDefault();
    if (saveStatus === 'unsaved' || saveStatus === 'error') {
      saveFile();
    }
  });

  // Ctrl+W / Cmd+W — close the active tab. Routes through the same close
  // flow as the X button so dirty prompts + neighbor activation match.
  useHotkeys('ctrl+w, cmd+w', async (e) => {
    e.preventDefault();
    const store = useOpenFilesStore.getState();
    const pid = store.currentProjectId;
    const slice = pid ? store.byProject.get(pid) : undefined;
    if (!slice?.activeId) return;
    const active = slice.open.find((f) => f.id === slice.activeId);
    if (!active) return;
    if (active.dirty) {
      const ok = window.confirm(`"${active.name}" has unsaved changes. Close anyway?`);
      if (!ok) return;
    }
    store.closeFile(active.id);
    const after = useOpenFilesStore.getState();
    const afterSlice = pid ? after.byProject.get(pid) : undefined;
    if (afterSlice?.activeId) {
      const next = afterSlice.open.find((f) => f.id === afterSlice.activeId);
      if (next) {
        await handleSelect({ id: next.id, name: next.name, path: next.path, type: 'file' });
      }
    } else {
      setSelectedFile(null);
      setEditedContent(undefined);
      setSaveStatus('saved');
    }
  });

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-expand folders when searching
  useEffect(() => {
    if (debouncedSearchQuery.trim()) {
      // Expand all folders that contain matches
      const expandAllMatchingFolders = (nodes: FileNodeWithContent[]): string[] => {
        const ids: string[] = [];
        for (const node of nodes) {
          if (node.type === 'folder') {
            ids.push(node.id);
            if (node.children) {
              ids.push(...expandAllMatchingFolders(node.children));
            }
          }
        }
        return ids;
      };

      const { filteredNodes } = filterFileTree(fileTree, debouncedSearchQuery);
      const idsToExpand = expandAllMatchingFolders(filteredNodes);
      setExpandedIds(new Set(idsToExpand));
    }
  }, [debouncedSearchQuery, fileTree]);

  // Load file tree on mount
  useEffect(() => {
    const loadFiles = async () => {
      if (!projectId) {
        // Show empty state if no project ID
        setFileTree([]);
        setError('No project selected');
        return;
      }

      setIsLoadingTree(true);
      setError(null);

      try {
        // Load files and git status in parallel
        const t0 = performance.now();
        const [files, status] = await Promise.all([
          backend.files.readProjectFiles(projectId),
          backend.files.getGitStatus(projectId),
        ]);
        logger.info(
          'FileTreePerf',
          `initial shallow load: ${(performance.now() - t0).toFixed(0)}ms (${files.length} root entries, ${status.length} git statuses)`,
        );

        setFileTree(files as FileNodeWithContent[]);

        // Convert git status array to a Map for quick lookups
        const statusMap = new Map<string, GitFileStatus['status']>();
        for (const file of status) {
          // Normalize path separators for comparison
          const normalizedPath = file.path.replace(/\\/g, '/');
          statusMap.set(normalizedPath, file.status);
        }
        setGitStatus(statusMap);

        // Build file index in background for full-project search
        useFileIndexStore.getState().buildIndex(backend, projectId);
      } catch (err) {
        logger.error('FilesTab', 'Failed to load project files:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load files';
        setError(errorMessage);
        // Show empty state on error instead of mock data
        setFileTree([]);
      } finally {
        setIsLoadingTree(false);
      }
    };

    // Clear index when switching projects, and reset the file tree store so
    // events from a previous project's watcher don't leak in.
    useFileIndexStore.getState().clear();
    useFileTreeStore.getState().reset();
    useFileTreeStore.getState().setProjectId(projectId ?? null);
    // Switch the multi-file tab strip to this project. The store keeps each
    // project's tabs in memory across switches; if we've never seen this
    // project before, hydrate from .ateliercode/cache/editor-state.json so
    // tabs survive app restarts too. After hydration, re-open whichever
    // file was active so the user lands back on the same buffer.
    useOpenFilesStore.getState().setProjectId(projectId ?? null);
    const restoreActiveFromStore = () => {
      if (!projectId) return;
      const slice = useOpenFilesStore.getState().byProject.get(projectId);
      const activeId = slice?.activeId;
      if (activeId) {
        const active = slice?.open.find((f) => f.id === activeId);
        if (active && handleSelectRef.current) {
          void handleSelectRef.current({
            id: active.id,
            name: active.name,
            path: active.path,
            type: 'file',
          });
          return;
        }
      }
      // No tab to restore — clear the editor surface.
      setSelectedFile(null);
      setEditedContent(undefined);
      setSaveStatus('saved');
    };
    if (projectId) {
      if (useOpenFilesStore.getState().byProject.has(projectId)) {
        // Already in memory — restore immediately.
        restoreActiveFromStore();
      } else {
        // Cache hit will hydrate the slice; then we can restore.
        void loadEditorStateForProject(backend, projectId).then(restoreActiveFromStore);
      }
    } else {
      setSelectedFile(null);
    }
    // Tear down the TS workspace — it'll re-init on the next editor mount
    // (or here if Monaco's already mounted, in which case the next file open
    // triggers a re-init via the editor's onMount cycle).
    tsWorkspace.dispose();
    // Reset and re-init the symbol index for the new project. It loads from
    // cache instantly, then refreshes in the background.
    useSymbolIndexStore.getState().reset();
    if (projectId) {
      const root = useProjectStore.getState().currentProject?.root_path;
      if (root) {
        void useSymbolIndexStore.getState().init(backend, projectId, root);
      }
    }
    loadFiles();
  }, [projectId, backend]);

  // Persist tab state for the current project on every change. The bridge
  // module debounces, so rapid clicks coalesce into one disk write.
  useEffect(() => {
    if (!projectId) return;
    const unsub = useOpenFilesStore.subscribe((state, prev) => {
      // Only persist when this project's slice actually changed.
      if (state.byProject.get(projectId) === prev.byProject.get(projectId)) return;
      scheduleEditorStateWrite(backend, projectId);
    });
    return () => {
      unsub();
      cancelEditorStateWrites(projectId);
    };
  }, [projectId, backend]);

  // Restore scroll + cursor whenever the active file's content lands. We
  // defer one frame so Monaco's setValue (which @monaco-editor/react does
  // asynchronously after the value prop changes) has time to commit.
  useEffect(() => {
    if (!selectedFile?.content || !editorRef.current || !projectId) return;
    const tab = useOpenFilesStore
      .getState()
      .byProject.get(projectId)
      ?.open.find((f) => f.id === selectedFile.id);
    if (!tab) return;
    if (tab.scrollTop === undefined && tab.cursorLine === undefined) return;
    const editor = editorRef.current;
    const handle = requestAnimationFrame(() => {
      if (!editor) return;
      try {
        if (tab.cursorLine !== undefined && tab.cursorColumn !== undefined) {
          editor.setPosition({ lineNumber: tab.cursorLine, column: tab.cursorColumn });
        }
        if (tab.scrollTop !== undefined) {
          editor.setScrollTop(tab.scrollTop);
        }
      } catch (e) {
        logger.debug('FilesTab', 'Scroll restore failed:', e);
      }
    });
    return () => cancelAnimationFrame(handle);
  }, [selectedFile?.id, selectedFile?.content, projectId]);

  // Background full-tree crawl. Kicks off shortly after the initial shallow
  // load so the user gets a fast first paint, then fills the tree cache in
  // the background. Once the crawl returns, expanding any folder is instant
  // (no roundtrip to the backend) because the children are already in the
  // store. Watcher events that landed during the crawl are preserved by the
  // mergeFullCrawl union semantics.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    // 1. Try to populate from cache immediately for an instant feel. The
    //    cache holds a full crawl from the previous session — the background
    //    refresh below validates it. This is what makes a 5000-file project
    //    open feel as snappy as a 50-file project.
    void (async () => {
      const cached = await readCache<CacheEnvelope<FileNode[]>>(
        projectId,
        CacheKeys.fileTree,
      );
      if (cancelled) return;
      if (!cached || cached.schemaVersion !== FILE_TREE_CACHE_VERSION) return;
      if (useFileTreeStore.getState().projectId !== projectId) return;
      useFileTreeStore.getState().mergeFullCrawl(cached.payload as FileNodeWithContent[]);
      logger.info(
        'FileTreePerf',
        `file tree from cache: ${countTreeNodes(cached.payload)} nodes (cached ${new Date(cached.cachedAt).toISOString()})`,
      );
    })();

    // 2. Always refresh in the background even if cache hit — files may have
    //    been edited between sessions. Status-bar pill flags the work.
    const handle = setTimeout(async () => {
      useCacheStatusStore.getState().begin('file-tree');
      try {
        const t0 = performance.now();
        const fullTree = await backend.files.crawlProjectTree(projectId);
        const fetchedMs = performance.now() - t0;
        if (cancelled) return;
        if (useFileTreeStore.getState().projectId !== projectId) return;
        const tMerge = performance.now();
        useFileTreeStore.getState().mergeFullCrawl(fullTree as FileNodeWithContent[]);
        const totalNodes = countTreeNodes(fullTree as FileNodeWithContent[]);
        logger.info(
          'FileTreePerf',
          `background crawl: fetch ${fetchedMs.toFixed(0)}ms + merge ${(performance.now() - tMerge).toFixed(0)}ms (${totalNodes} total nodes, ${fullTree.length} root)`,
        );
        // Persist the fresh result for next session's instant load.
        await writeCache(projectId, CacheKeys.fileTree, envelope(fullTree, FILE_TREE_CACHE_VERSION));
      } catch (err) {
        // Non-fatal — lazy expansion still works.
        logger.debug('FilesTab', 'Background crawl failed:', err);
      } finally {
        useCacheStatusStore.getState().end('file-tree');
      }
    }, 250); // brief delay so initial load + git status get the IO first

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [projectId, backend]);

  // NOTE: live file-tree updates (new files appearing with an amber flash,
  // live git-status) rode the Tauri `watch_project_files` + `file-tree-event`
  // channel, which isn't routed through the core yet — so this was Tauri-only
  // and is inert under the CEF host. The tree still refreshes on manual reload
  // and explicit actions; live auto-update awaits a `file.subscribe` core
  // handler (a separate ticket).

  // Helper function to update children in the file tree
  const updateNodeChildren = (
    nodes: FileNodeWithContent[],
    nodeId: string,
    children: FileNodeWithContent[]
  ): FileNodeWithContent[] => {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return { ...node, children };
      }
      if (node.children) {
        return { ...node, children: updateNodeChildren(node.children, nodeId, children) };
      }
      return node;
    });
  };

  /**
   * Expand the file tree to a target path by loading each ancestor folder's
   * children on-demand. Returns the target node if found.
   */
  const expandToPath = useCallback(async (targetPath: string): Promise<FileNodeWithContent | null> => {
    if (!projectId) return null;

    const normTarget = targetPath.replace(/\\/g, '/').toLowerCase();

    // Find the project root from the top-level nodes
    const tree = fileTreeRef.current;
    if (tree.length === 0) return null;

    // Get project root from the first node's path
    const firstNodePath = tree[0].path.replace(/\\/g, '/');
    // root_path is the common parent of all top-level nodes
    const rootIdx = firstNodePath.lastIndexOf('/' + tree[0].name);
    const projectRoot = rootIdx >= 0 ? firstNodePath.substring(0, rootIdx) : '';
    const normRoot = projectRoot.toLowerCase();

    // Compute relative segments from project root
    let relativePath = normTarget;
    if (normTarget.startsWith(normRoot + '/')) {
      relativePath = normTarget.substring(normRoot.length + 1);
    } else if (normTarget.startsWith(normRoot + '\\')) {
      relativePath = normTarget.substring(normRoot.length + 1);
    }
    const segments = relativePath.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    let currentNodes = fileTreeRef.current;

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const isLast = i === segments.length - 1;

      // Find matching node at this level
      const match = currentNodes.find(
        (n) => n.name.toLowerCase() === seg
      );

      if (!match) return null; // path segment not found

      if (isLast) {
        // Found the target file/folder
        return match;
      }

      // It's a folder — ensure its children are loaded
      if (match.type !== 'folder') return null;

      if (!match.children) {
        // Load children from backend
        try {
          const children = await backend.files.getFolderChildren(projectId, match.path, match.gitignored);
          const typedChildren = children as FileNodeWithContent[];
          // Update both state and ref immediately so next loop iteration can see the children
          const updatedTree = updateNodeChildren(fileTreeRef.current, match.id, typedChildren);
          fileTreeRef.current = updatedTree;
          setFileTree(updatedTree);
          currentNodes = typedChildren;
        } catch (err) {
          logger.error('FilesTab', 'Failed to expand path segment:', err);
          return null;
        }
      } else {
        currentNodes = match.children;
      }
    }

    return null;
  }, [projectId, backend]);

  const handleToggle = async (id: string, node: FileNodeWithContent) => {
    // If collapsing, just toggle the expanded state
    if (expandedIds.has(id)) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }

    // If expanding and children not loaded, fetch them
    if (node.type === 'folder' && !node.children && node.hasChildren && projectId) {
      setLoadingIds((prev) => new Set(prev).add(id));

      try {
        const t0 = performance.now();
        const children = await backend.files.getFolderChildren(projectId, node.path, node.gitignored);
        logger.info(
          'FileTreePerf',
          `lazy folder expand "${node.id}": ${(performance.now() - t0).toFixed(0)}ms (${children.length} children)`,
        );
        // Update the file tree with the loaded children
        setFileTree((prev) => updateNodeChildren(prev, id, children as FileNodeWithContent[]));
      } catch (err) {
        logger.error('FilesTab', 'Failed to load folder children:', err);
        setError(err instanceof Error ? err.message : 'Failed to load folder contents');
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }

    // Expand the folder
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleSelect = async (node: FileNodeWithContent) => {
    logger.info('FilesTab', `handleSelect start: ${node.id} (type=${node.type})`);
    // Flush pending save before switching files
    if (saveStatus === 'unsaved' && editedContent !== undefined && selectedFile) {
      logger.info('FilesTab', `flushing pending save for ${selectedFile.id}`);
      await saveFile();
      logger.info('FilesTab', `save done`);
    }

    // Reset edit state for new file
    setEditedContent(undefined);
    setSaveStatus('saved');
    setSaveError(null);
    setImageBase64(null);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });

    setSelectedFile(node);

    // Add the file to the editor tab strip (or activate if already open).
    // Folders never get tabs; opening a file from the tree both activates
    // its tab and triggers the content load below.
    if (node.type === 'file') {
      useOpenFilesStore.getState().openFile({
        id: node.id,
        path: node.path,
        name: node.name,
      });
    }

    // Only load content for files, not folders
    if (node.type !== 'file') {
      return;
    }

    // Load image files as base64
    if (isImageFile(node.name)) {
      if (projectId) {
        setIsLoadingContent(true);
        setError(null);
        try {
          const base64Data = await backend.files.readFileBase64(projectId, node.path);
          setImageBase64(base64Data);
          setSelectedFile({ ...node, content: undefined });
        } catch (err) {
          logger.error('FilesTab', 'Failed to load image:', err);
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setImageBase64(null);
        } finally {
          setIsLoadingContent(false);
        }
      }
      return;
    }

    // Don't try to load other binary files
    if (isBinaryFile(node.name)) {
      setSelectedFile({ ...node, content: undefined });
      return;
    }

    // Check file size limit (1MB = 1,048,576 bytes)
    if (node.size && node.size > 1048576) {
      setSelectedFile({ ...node, content: undefined });
      setError('File is too large to display (> 1MB)');
      return;
    }

    // If it's a file and we have a project ID, load its content
    if (projectId && !node.content) {
      logger.info('FilesTab', `fetching content: ${node.id}`);
      setIsLoadingContent(true);
      setError(null);
      try {
        const content = await backend.files.readFile(projectId, node.path);
        // Update the selected file with content
        setSelectedFile({ ...node, content });
        logger.info('FilesTab', `content set: ${node.id} (${content.length} chars)`);
        // Lazy-load: pull this file's relative imports into the TS workspace
        // so go-to-def + completions work for them. Cheap no-op for non-TS
        // files. Fire-and-forget — don't block the UI.
        void tsWorkspace.ensureFileImports(node.id, content);
      } catch (err) {
        logger.error('FilesTab', 'Failed to load file content:', err);
        setError(err instanceof Error ? err.message : 'Failed to load file content');
        // Show file without content on error
        setSelectedFile({ ...node, content: undefined });
      } finally {
        setIsLoadingContent(false);
        logger.info('FilesTab', `loading=false: ${node.id}`);
      }
    } else {
      logger.info('FilesTab', `no fetch needed: ${node.id} (projectId=${projectId} hasContent=${!!node.content})`);
    }
  };

  // Keep ref updated for use in subscription
  handleSelectRef.current = handleSelect;

  const toggleEditor = () => {
    setEditorCollapsed(!editorCollapsed);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  // ─── Drag-drop handlers ────────────────────────────────────────────────

  const refreshTreeAfterMutation = useCallback(async () => {
    if (!projectId) return;
    try {
      const fullTree = await backend.files.crawlProjectTree(projectId);
      // Reset the children of every loaded folder by re-merging from a fresh
      // crawl. Reset projectId guard so concurrent project switches don't
      // clobber the tree.
      if (useFileTreeStore.getState().projectId !== projectId) return;
      // Hard-replace: a move/copy may have removed nodes, which mergeFullCrawl
      // wouldn't catch (it preserves existing-only items). Setting fresh is
      // simpler and the watcher's per-path events keep us up-to-date between
      // mutations anyway.
      useFileTreeStore.getState().setTree(fullTree as FileNodeWithContent[]);
    } catch (err) {
      logger.warn('FilesTab', 'Refresh after mutation failed:', err);
    }
  }, [projectId, backend]);

  const handleDragEnterFolder = useCallback((folderId: string) => {
    setDropTargetId(folderId);
    if (hoverExpandTimerRef.current) clearTimeout(hoverExpandTimerRef.current);
    // Hover-to-expand: if the user holds the drag over a collapsed folder for
    // 400ms, open it so they can drop deeper. Matches WebStorm/Finder UX.
    hoverExpandTimerRef.current = setTimeout(() => {
      hoverExpandTimerRef.current = null;
      const expanded = useFileTreeStore.getState().expandedIds;
      if (!expanded.has(folderId)) {
        setExpandedIds((prev) => new Set(prev).add(folderId));
      }
    }, 400);
  }, []);

  const handleDragLeaveFolder = useCallback((folderId: string) => {
    if (dropTargetIdRef.current === folderId) {
      setDropTargetId(null);
    }
    if (hoverExpandTimerRef.current) {
      clearTimeout(hoverExpandTimerRef.current);
      hoverExpandTimerRef.current = null;
    }
  }, []);

  /**
   * Process a single external file/folder drop. Checks for a name collision
   * in the destination — if there is one, queues a conflict prompt and
   * returns false (caller should stop processing the queue until the user
   * resolves the prompt). Otherwise copies and refreshes.
   */
  const processExternalDrop = useCallback(
    async (sourcePath: string, destFolderRel: string): Promise<boolean> => {
      if (!projectId) return true;
      const normSrc = sourcePath.replace(/\\/g, '/');
      const fileName = normSrc.substring(normSrc.lastIndexOf('/') + 1);
      if (!fileName) return true;

      const targetRel = destFolderRel ? `${destFolderRel}/${fileName}` : fileName;
      try {
        const exists = await backend.files.pathExistsInProject(projectId, targetRel);
        if (exists) {
          // Pause the queue and surface the modal — the user picks
          // overwrite/keepboth/cancel and the resolution handler resumes.
          setConflictPrompt({ sourcePath, destFolderRel, fileName, remaining: [] });
          return false;
        }
        await backend.files.copyExternalIntoProject(
          projectId,
          sourcePath,
          destFolderRel,
          'overwrite', // safe default since we just confirmed no conflict
        );
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Copy failed';
        showToast(msg, 'error');
        return true; // continue processing the rest
      }
    },
    [projectId, backend],
  );

  /**
   * Drain the queued external drops one at a time. Stops when one of them
   * triggers a conflict prompt — the modal resume path will call back in.
   */
  const drainExternalDropQueue = useCallback(async () => {
    while (dropQueueRef.current.length > 0) {
      const next = dropQueueRef.current.shift();
      if (!next) continue;
      const ok = await processExternalDrop(next.sourcePath, next.destFolderRel);
      if (!ok) return; // paused for user input
    }
    await refreshTreeAfterMutation();
  }, [processExternalDrop, refreshTreeAfterMutation]);

  // NOTE: importing external OS files via drag-and-drop rode Tauri's webview
  // `onDragDropEvent` (the only path that delivered real OS file paths, since
  // HTML5 dataTransfer.files is empty in the embedded webview). The CEF host
  // doesn't surface OS drop paths yet, so external file drop is inert under the
  // migrated app and awaits a host drag-drop bridge (a separate ticket).
  // Internal (within-tree) drag-and-drop below is unaffected.

  const handleInternalDrop = useCallback(
    async (sourceId: string, destFolderId: string) => {
      if (!projectId) return;
      // Forbid drop onto self or an existing parent (no-op).
      if (sourceId === destFolderId) return;
      // sourceId is the relative path; destFolderId is too (or "" for root).
      const sourceParent = sourceId.includes('/')
        ? sourceId.substring(0, sourceId.lastIndexOf('/'))
        : '';
      if (sourceParent === destFolderId) return;
      // Forbid moving a folder into itself or a descendant.
      if (destFolderId.startsWith(sourceId + '/')) {
        showToast("Can't move a folder into itself", 'error');
        return;
      }
      try {
        await backend.files.moveWithinProject(projectId, sourceId, destFolderId);
        await refreshTreeAfterMutation();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Move failed';
        showToast(msg, 'error');
      }
    },
    [projectId, backend, refreshTreeAfterMutation],
  );

  // Cleanup hover-expand timer on unmount.
  useEffect(() => {
    return () => {
      if (hoverExpandTimerRef.current) clearTimeout(hoverExpandTimerRef.current);
    };
  }, []);

  // Context menu handlers
  const handleContextMenu = (e: React.MouseEvent, node: FileNodeWithContent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, node });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      logger.error('FilesTab', 'Failed to copy to clipboard:', err);
    }
    closeContextMenu();
  };

  const copyPath = () => {
    if (contextMenu) {
      copyToClipboard(contextMenu.node.path);
    }
  };

  const copyName = () => {
    if (contextMenu) {
      copyToClipboard(contextMenu.node.name);
    }
  };

  const copyRelativePath = () => {
    if (contextMenu && projectId) {
      // Remove project path prefix to get relative path
      const relativePath = contextMenu.node.path.replace(/\\/g, '/');
      copyToClipboard(relativePath);
    }
  };

  const revealInExplorer = async () => {
    if (contextMenu) {
      try {
        await backend.files.revealInExplorer(contextMenu.node.path);
      } catch (err) {
        logger.error('FilesTab', 'Failed to reveal in explorer:', err);
      }
      closeContextMenu();
    }
  };

  const exportAsPdf = async () => {
    if (!contextMenu || !projectId) return;
    const node = contextMenu.node;
    closeContextMenu();
    try {
      const content = await backend.files.readFile(projectId, node.path);
      const htmlBody = await marked(content, { gfm: true, breaks: true });
      const title = node.name.replace(/\.md$/i, '');
      const styledHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { size: letter; margin: 1in; }
  body {
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 12pt;
    line-height: 1.6;
    color: #1a1a1a;
    max-width: 100%;
    padding: 0;
    margin: 0;
  }
  h1 { font-size: 24pt; margin: 0 0 12pt; border-bottom: 1px solid #ddd; padding-bottom: 6pt; }
  h2 { font-size: 18pt; margin: 20pt 0 8pt; border-bottom: 1px solid #eee; padding-bottom: 4pt; }
  h3 { font-size: 14pt; margin: 16pt 0 6pt; }
  h4, h5, h6 { font-size: 12pt; margin: 12pt 0 4pt; }
  p { margin: 0 0 8pt; }
  code { background: #f4f4f4; padding: 1pt 4pt; border-radius: 3pt; font-size: 10.5pt; font-family: 'Cascadia Code', 'Fira Code', Consolas, monospace; }
  pre { background: #f6f8fa; border: 1px solid #e1e4e8; border-radius: 4pt; padding: 12pt; overflow-x: auto; margin: 8pt 0; }
  pre code { background: none; padding: 0; font-size: 10pt; }
  blockquote { border-left: 3pt solid #ddd; margin: 8pt 0; padding: 4pt 12pt; color: #555; }
  table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
  th, td { border: 1px solid #ddd; padding: 6pt 10pt; text-align: left; }
  th { background: #f6f8fa; font-weight: 600; }
  ul, ol { margin: 4pt 0 8pt; padding-left: 24pt; }
  li { margin: 2pt 0; }
  img { max-width: 100%; height: auto; }
  hr { border: none; border-top: 1px solid #ddd; margin: 16pt 0; }
  a { color: #0366d6; text-decoration: none; }
  input[type="checkbox"] { margin-right: 6pt; }
</style>
</head>
<body>${htmlBody}</body>
</html>`;
      const printWindow = window.open('', '_blank', 'width=816,height=1056');
      if (!printWindow) {
        logger.error('FilesTab', 'Failed to open print window — popup may be blocked');
        return;
      }
      printWindow.document.write(styledHtml);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
      // Fallback if onload doesn't fire (already loaded)
      setTimeout(() => {
        if (!printWindow.closed) {
          printWindow.print();
        }
      }, 500);
    } catch (err) {
      logger.error('FilesTab', 'Failed to export markdown as PDF:', err);
    }
  };

  const startRename = () => {
    if (contextMenu) {
      setRenameTarget(contextMenu.node);
      setRenameValue(contextMenu.node.name);
      closeContextMenu();
    }
  };

  const submitRename = async () => {
    if (!renameTarget || !projectId || !renameValue.trim()) return;
    if (renameValue.trim() === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    try {
      await backend.files.renameFile(projectId, renameTarget.path, renameValue.trim());
      // Reload file tree
      const [files, status] = await Promise.all([
        backend.files.readProjectFiles(projectId),
        backend.files.getGitStatus(projectId),
      ]);
      setFileTree(files as FileNodeWithContent[]);
      const statusMap = new Map<string, GitFileStatus['status']>();
      for (const file of status) statusMap.set(file.path.replace(/\\/g, '/'), file.status);
      setGitStatus(statusMap);
      if (selectedFile?.id === renameTarget.id) setSelectedFile(null);
    } catch (err) {
      logger.error('FilesTab', 'Failed to rename:', err);
      setError(err instanceof Error ? err.message : 'Failed to rename');
    }
    setRenameTarget(null);
  };

  const deleteNode = async () => {
    if (!contextMenu || !projectId) return;
    const node = contextMenu.node;
    const isFolder = node.type === 'folder';
    closeContextMenu();

    // Simple confirmation
    if (!window.confirm(`Delete ${isFolder ? 'folder' : 'file'} "${node.name}"${isFolder ? ' and all its contents' : ''}?`)) return;

    try {
      await backend.files.deleteFile(projectId, node.path);
      // Reload file tree
      const [files, status] = await Promise.all([
        backend.files.readProjectFiles(projectId),
        backend.files.getGitStatus(projectId),
      ]);
      setFileTree(files as FileNodeWithContent[]);
      const statusMap = new Map<string, GitFileStatus['status']>();
      for (const file of status) statusMap.set(file.path.replace(/\\/g, '/'), file.status);
      setGitStatus(statusMap);
      if (selectedFile?.id === node.id) setSelectedFile(null);
    } catch (err) {
      logger.error('FilesTab', 'Failed to delete:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const startCreate = (type: 'file' | 'folder') => {
    if (!contextMenu) return;
    const parent = contextMenu.node.type === 'folder' ? contextMenu.node : null;
    if (!parent) {
      // If right-clicked on a file, use its parent directory
      // We'll use the project root as fallback
      closeContextMenu();
      return;
    }
    setCreateTarget({ parent, type });
    setCreateValue('');
    closeContextMenu();
  };

  const submitCreate = async () => {
    if (!createTarget || !projectId || !createValue.trim()) return;
    try {
      if (createTarget.type === 'file') {
        await backend.files.createFile(projectId, createTarget.parent.path, createValue.trim());
      } else {
        await backend.files.createFolder(projectId, createTarget.parent.path, createValue.trim());
      }
      // Reload file tree
      const [files, status] = await Promise.all([
        backend.files.readProjectFiles(projectId),
        backend.files.getGitStatus(projectId),
      ]);
      setFileTree(files as FileNodeWithContent[]);
      const statusMap = new Map<string, GitFileStatus['status']>();
      for (const file of status) statusMap.set(file.path.replace(/\\/g, '/'), file.status);
      setGitStatus(statusMap);
      // Expand the parent folder
      setExpandedIds((prev) => new Set([...prev, createTarget.parent.id]));
    } catch (err) {
      logger.error('FilesTab', 'Failed to create:', err);
      setError(err instanceof Error ? err.message : 'Failed to create');
    }
    setCreateTarget(null);
  };

  // Handle markdown link clicks - navigate to local files
  const handleMarkdownLinkClick = useCallback((href: string) => {
    if (!selectedFile || !href) return;

    // Skip external links
    if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    // Handle anchor links (scroll within page - not implemented, just ignore)
    if (href.startsWith('#')) {
      return;
    }

    // Resolve relative path from current file's directory
    const currentDir = selectedFile.path.replace(/\\/g, '/').split('/').slice(0, -1).join('/');
    let targetPath = href.replace(/\\/g, '/');

    // Remove any anchor part from the path
    targetPath = targetPath.split('#')[0];

    if (!targetPath.startsWith('/')) {
      // Relative path - resolve from current directory
      const parts = currentDir.split('/');
      const hrefParts = targetPath.split('/');

      for (const part of hrefParts) {
        if (part === '..') {
          parts.pop();
        } else if (part !== '.') {
          parts.push(part);
        }
      }
      targetPath = parts.join('/');
    }

    // Find the file in our tree
    const findNode = (nodes: FileNodeWithContent[], path: string): FileNodeWithContent | null => {
      for (const node of nodes) {
        const normalizedNodePath = node.path.replace(/\\/g, '/');
        if (normalizedNodePath === path || normalizedNodePath.endsWith('/' + path)) {
          return node;
        }
        if (node.children) {
          const found = findNode(node.children, path);
          if (found) return found;
        }
      }
      return null;
    };

    const targetNode = findNode(fileTree, targetPath);
    if (targetNode) {
      handleSelect(targetNode);
    } else {
      logger.warn('FilesTab', 'Could not find linked file:', targetPath);
    }
  }, [selectedFile, fileTree]);

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => closeContextMenu();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  // Handle file open requests from terminal/other components
  useEffect(() => {
    return useFileOpenStore.subscribe((state, prevState) => {
      const request = state.pendingRequest;
      if (!request || request === prevState.pendingRequest) return;

      let targetPath = request.path;

      // Resolve relative paths against project root
      if (request.projectRoot && !targetPath.startsWith('/') && !targetPath.match(/^[A-Za-z]:/)) {
        targetPath = `${request.projectRoot}/${targetPath}`.replace(/\\/g, '/');
      }

      // Normalize path
      targetPath = targetPath.replace(/\\/g, '/');

      // Find node by path
      const findNode = (nodes: FileNodeWithContent[], path: string): FileNodeWithContent | null => {
        for (const node of nodes) {
          const normalizedNodePath = node.path.replace(/\\/g, '/');
          if (normalizedNodePath === path || normalizedNodePath.endsWith('/' + path)) {
            return node;
          }
          if (node.children) {
            const found = findNode(node.children, path);
            if (found) return found;
          }
        }
        return null;
      };

      const targetNode = findNode(fileTree, targetPath);
      if (targetNode && handleSelectRef.current) {
        handleSelectRef.current(targetNode);
      } else if (!targetNode) {
        // File not in loaded tree — try expanding the path dynamically
        expandToPath(targetPath).then((node) => {
          if (node && handleSelectRef.current) {
            // Expand all parent folders in the UI
            const normPath = node.path.replace(/\\/g, '/');
            const segments = normPath.split('/');
            const newExpanded: string[] = [];
            // Build parent path IDs by finding them in the now-loaded tree
            let searchNodes = fileTreeRef.current;
            for (let i = 0; i < segments.length - 1; i++) {
              const seg = segments[i];
              const match = searchNodes.find((n) => n.name.toLowerCase() === seg.toLowerCase());
              if (match && match.type === 'folder') {
                newExpanded.push(match.id);
                searchNodes = match.children ?? [];
              }
            }
            if (newExpanded.length > 0) {
              setExpandedIds((prev) => {
                const next = new Set(prev);
                newExpanded.forEach((id) => next.add(id));
                return next;
              });
            }
            handleSelectRef.current(node);
          } else {
            logger.warn('FilesTab', 'Could not find file from terminal link:', targetPath);
          }
        });
      }

      // Clear the request
      useFileOpenStore.getState().clearRequest();
    });
  }, [fileTree]);

  // Get breadcrumb path
  const getBreadcrumbs = (): string[] => {
    if (!selectedFile) return [];
    return selectedFile.path.split(/[/\\]/).filter(Boolean);
  };

  // Get filtered file tree and match count
  // Use full index for search when available, fall back to loaded tree
  const indexEntries = useFileIndexStore((s) => s.entries);
  const { filteredNodes: displayedFileTree, matchCount, indexResults } = (() => {
    if (!debouncedSearchQuery.trim()) {
      return { ...filterFileTree(fileTree, ''), indexResults: null };
    }
    // When index is available, use it for comprehensive search
    if (indexEntries.length > 0) {
      const results = useFileIndexStore.getState().search(debouncedSearchQuery, 100);
      return {
        filteredNodes: [],
        matchCount: results.length,
        indexResults: results.map((e) => ({
          id: e.path,
          name: e.name,
          path: e.path,
          type: e.type as 'file' | 'folder',
        } as FileNodeWithContent)),
      };
    }
    // Fallback to tree filter
    return { ...filterFileTree(fileTree, debouncedSearchQuery), indexResults: null };
  })();

  // Detect mobile for layout direction
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <>
      <div className="card bg-base-200 h-full">
        <PanelGroup direction={isMobile ? 'vertical' : 'horizontal'} className="h-full" autoSaveId="files-panels">
          {/* Left side - File tree */}
          <Panel defaultSize={30} minSize={20} maxSize={editorCollapsed ? 100 : 50}>
          <div className="border-r border-base-300 flex flex-col h-full">
            {/* Search bar */}
            <div className="p-4 border-b border-base-300">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/50" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search files... (Ctrl/Cmd+F)"
                    className="input input-sm input-bordered w-full pl-9 pr-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                      onClick={clearSearch}
                      title="Clear search"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <FilesPopoutButton projectId={projectId} />
              </div>
              {debouncedSearchQuery && (
                <div className="mt-2 text-xs text-base-content/60">
                  {matchCount === 0 ? (
                    <span className="text-warning">No matches found</span>
                  ) : (
                    <span>
                      {matchCount} {matchCount === 1 ? 'match' : 'matches'} found
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="alert alert-warning m-2">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* File tree — also acts as the OS-drop hit-test target */}
            <div
              ref={filesPanelRef}
              className="flex-1 overflow-y-auto p-2"
            >
              {isLoadingTree ? (
                <div className="flex items-center justify-center h-full">
                  <span className="loading loading-spinner loading-md"></span>
                </div>
              ) : indexResults && indexResults.length > 0 ? (
                /* Flat index search results */
                indexResults.map((node) => (
                  <button
                    key={node.id}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-left hover:bg-base-300 transition-colors ${
                      selectedFile?.path === node.path ? 'bg-primary/20' : ''
                    }`}
                    onClick={async () => {
                      // Try to find in loaded tree first
                      const findNode = (nodes: FileNodeWithContent[], path: string): FileNodeWithContent | null => {
                        for (const n of nodes) {
                          if (n.path.replace(/\\/g, '/') === path.replace(/\\/g, '/')) return n;
                          if (n.children) { const f = findNode(n.children, path); if (f) return f; }
                        }
                        return null;
                      };
                      const existing = findNode(fileTree, node.path);
                      if (existing) {
                        handleSelect(existing);
                      } else {
                        // Expand path and then select
                        const expanded = await expandToPath(node.path);
                        if (expanded) {
                          handleSelect(expanded);
                        }
                      }
                    }}
                  >
                    {node.type === 'folder'
                      ? iconPack.getFolderIcon(node.name, false)
                      : iconPack.getFileIcon(node.name)}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{node.name}</div>
                      <div className="text-xs text-base-content/40 truncate">{node.path}</div>
                    </div>
                  </button>
                ))
              ) : displayedFileTree.length === 0 ? (
                <div className="flex items-center justify-center h-full text-base-content/50">
                  <div className="text-center max-w-md px-4">
                    {debouncedSearchQuery ? (
                      <>
                        <Search className="w-12 h-12 mx-auto mb-2 opacity-20" />
                        <p className="text-sm font-medium mb-2">No files match your search</p>
                        <p className="text-xs">
                          Try a different search term or{' '}
                          <button
                            className="link link-primary"
                            onClick={clearSearch}
                          >
                            clear the search
                          </button>
                        </p>
                      </>
                    ) : fileTree.length === 0 ? (
                      <>
                        <Folder className="w-12 h-12 mx-auto mb-2 opacity-20" />
                        {error ? (
                          <>
                            <p className="text-sm font-medium mb-2">Unable to load files</p>
                            <p className="text-xs text-error mb-3">{error}</p>
                            {error.includes('Project not found') && (
                              <p className="text-xs">
                                This project no longer exists. Please create a new project or open an existing one from the home page.
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm">No files found</p>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              ) : (
                displayedFileTree.map((node) => (
                  <FileTreeItem
                    key={node.id}
                    node={node}
                    level={0}
                    selectedId={selectedFile?.id || null}
                    expandedIds={expandedIds}
                    loadingIds={loadingIds}
                    flashIds={flashIds}
                    iconPack={iconPack}
                    dropTargetId={dropTargetId}
                    onInternalDrop={handleInternalDrop}
                    onDragEnterFolder={handleDragEnterFolder}
                    onDragLeaveFolder={handleDragLeaveFolder}
                    gitStatus={gitStatus}
                    onSelect={handleSelect}
                    onToggle={handleToggle}
                    onContextMenu={handleContextMenu}
                    searchQuery={debouncedSearchQuery}
                  />
                ))
              )}
            </div>
          </div>
          </Panel>

          {/* Right side - Monaco Editor */}
          {!editorCollapsed && (
            <>
            <PanelResizeHandle className={`${isMobile ? 'h-2 cursor-row-resize' : 'w-2 cursor-col-resize'} bg-base-300 hover:bg-primary transition-colors`} />
            <Panel defaultSize={70} minSize={50}>
            <div className="bg-base-100 flex flex-col h-full">
              {/* Tab strip — sits above the breadcrumb header so users can see
                  every open file at a glance and switch between them. */}
              <EditorTabs
                iconPack={iconPack}
                onSwitchTo={async (tab) => {
                  // Re-use the file-tree handleSelect path so save-flush,
                  // image/binary handling, and content fetch all run the
                  // same way regardless of where the click came from.
                  const synthetic: FileNodeWithContent = {
                    id: tab.id,
                    name: tab.name,
                    path: tab.path,
                    type: 'file',
                  };
                  await handleSelect(synthetic);
                }}
                onAfterClose={async (newActive: OpenFile | null) => {
                  if (!newActive) {
                    // Last tab closed — clear the editor.
                    setSelectedFile(null);
                    setEditedContent(undefined);
                    setSaveStatus('saved');
                    return;
                  }
                  const synthetic: FileNodeWithContent = {
                    id: newActive.id,
                    name: newActive.name,
                    path: newActive.path,
                    type: 'file',
                  };
                  await handleSelect(synthetic);
                }}
              />
              {selectedFile ? (
                <>
                  {/* Header with breadcrumbs and file info */}
                  <div className="border-b border-base-300 p-3 bg-base-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-xs text-base-content/60">
                        {getBreadcrumbs().map((crumb, index, arr) => (
                          <div key={index} className="flex items-center gap-2">
                            <span>{crumb}</span>
                            {index < arr.length - 1 && (
                              <ChevronRight className="w-3 h-3" />
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={toggleEditor}
                        title="Hide editor"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      {iconPack.getFileIcon(selectedFile.name)}
                      <h3 className="font-semibold">
                        {selectedFile.name}
                        {saveStatus === 'unsaved' && <span className="text-warning ml-1">*</span>}
                      </h3>

                      {/* Preview/edit toggle for renderable formats (markdown, mermaid). */}
                      {selectedFile.type === 'file'
                        && (isMarkdownFile(selectedFile.name) || isMermaidFile(selectedFile.name))
                        && selectedFile.content !== undefined && (
                        <div className="flex items-center gap-1 bg-base-300 rounded-lg p-0.5">
                          <button
                            className={`btn btn-xs gap-1 ${markdownViewMode === 'preview' ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setMarkdownViewMode('preview')}
                            title="Preview rendered output"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Preview
                          </button>
                          <button
                            className={`btn btn-xs gap-1 ${markdownViewMode === 'edit' ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setMarkdownViewMode('edit')}
                            title="Edit source"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Edit
                          </button>
                        </div>
                      )}

                      {/* Save status indicator */}
                      {selectedFile.type === 'file' && !isBinaryFile(selectedFile.name) && selectedFile.content !== undefined && (
                        <div className="flex items-center gap-1.5 text-xs ml-auto">
                          {saveStatus === 'unsaved' && (
                            <span className="flex items-center gap-1 text-warning">
                              <span className="w-2 h-2 rounded-full bg-warning inline-block" />
                              Unsaved
                            </span>
                          )}
                          {saveStatus === 'saving' && (
                            <span className="flex items-center gap-1 text-info">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              Saving...
                            </span>
                          )}
                          {saveStatus === 'saved' && (
                            <span className="flex items-center gap-1 text-success">
                              <Check className="w-3 h-3" />
                              Saved
                            </span>
                          )}
                          {saveStatus === 'error' && (
                            <span className="flex items-center gap-1 text-error" title={saveError || 'Save failed'}>
                              <AlertTriangle className="w-3 h-3" />
                              Save failed
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-base-content/70">
                      {selectedFile.size && (
                        <div className="flex items-center gap-1">
                          <HardDrive className="w-3 h-3" />
                          <span>{formatFileSize(selectedFile.size)}</span>
                        </div>
                      )}
                      {selectedFile.modified && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(selectedFile.modified)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Editor content */}
                  <div className="flex-1 overflow-hidden">
                    {isLoadingContent ? (
                      <div className="h-full flex items-center justify-center">
                        <span className="loading loading-spinner loading-lg"></span>
                      </div>
                    ) : selectedFile.type === 'folder' ? (
                      <div className="h-full flex items-center justify-center text-base-content/50">
                        <div className="text-center">
                          <Folder className="w-16 h-16 mx-auto mb-4 opacity-20" />
                          <p className="font-semibold text-lg text-base-content">
                            {selectedFile.name}
                          </p>
                          <p className="text-sm mt-2">Folder</p>
                        </div>
                      </div>
                    ) : isImageFile(selectedFile.name) && imageBase64 ? (
                      <div className="h-full relative bg-[#1e1e1e] select-none">
                        {/* Zoom controls */}
                        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-base-300/80 backdrop-blur-sm rounded-lg p-1">
                          <button
                            className="btn btn-ghost btn-xs btn-square"
                            onClick={() => { setImageZoom(z => Math.max(0.1, z / 1.3)); }}
                            title="Zoom out"
                          >
                            <ZoomOut className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-xs font-mono w-12 text-center">
                            {Math.round(imageZoom * 100)}%
                          </span>
                          <button
                            className="btn btn-ghost btn-xs btn-square"
                            onClick={() => { setImageZoom(z => Math.min(20, z * 1.3)); }}
                            title="Zoom in"
                          >
                            <ZoomIn className="w-3.5 h-3.5" />
                          </button>
                          <div className="w-px h-4 bg-base-content/20 mx-0.5" />
                          <button
                            className="btn btn-ghost btn-xs btn-square"
                            onClick={() => { setImageZoom(1); setImagePan({ x: 0, y: 0 }); }}
                            title="Reset view"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {/* Pannable + zoomable image area */}
                        <div
                          ref={imageContainerRef}
                          className="h-full w-full overflow-hidden"
                          style={{ cursor: isPanning ? 'grabbing' : imageZoom > 1 ? 'grab' : 'default' }}
                          onWheel={(e) => {
                            e.preventDefault();
                            const delta = e.deltaY > 0 ? 1 / 1.15 : 1.15;
                            setImageZoom(z => Math.min(20, Math.max(0.1, z * delta)));
                          }}
                          onMouseDown={(e) => {
                            if (e.button !== 0) return;
                            setIsPanning(true);
                            panStartRef.current = { x: e.clientX, y: e.clientY, panX: imagePan.x, panY: imagePan.y };
                          }}
                          onMouseMove={(e) => {
                            if (!isPanning) return;
                            const dx = e.clientX - panStartRef.current.x;
                            const dy = e.clientY - panStartRef.current.y;
                            setImagePan({ x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy });
                          }}
                          onMouseUp={() => setIsPanning(false)}
                          onMouseLeave={() => setIsPanning(false)}
                        >
                          <div
                            className="h-full w-full flex items-center justify-center"
                            style={{
                              transform: `translate(${imagePan.x}px, ${imagePan.y}px) scale(${imageZoom})`,
                              transformOrigin: 'center center',
                              transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                            }}
                          >
                            <img
                              src={`data:${getImageMimeType(selectedFile.name)};base64,${imageBase64}`}
                              alt={selectedFile.name}
                              className="max-w-full max-h-full object-contain pointer-events-none"
                              draggable={false}
                            />
                          </div>
                        </div>
                      </div>
                    ) : isBinaryFile(selectedFile.name) ? (
                      <div className="h-full flex items-center justify-center text-base-content/50">
                        <div className="text-center">
                          <FileImage className="w-16 h-16 mx-auto mb-4 opacity-20" />
                          <p className="font-semibold text-lg text-base-content">
                            Binary File
                          </p>
                          <p className="text-sm mt-2">Cannot display binary files</p>
                        </div>
                      </div>
                    ) : selectedFile.size && selectedFile.size > 1048576 ? (
                      <div className="h-full flex items-center justify-center text-base-content/50">
                        <div className="text-center">
                          <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
                          <p className="font-semibold text-lg text-base-content">
                            File Too Large
                          </p>
                          <p className="text-sm mt-2">
                            Files larger than 1MB cannot be displayed
                          </p>
                          <p className="text-xs mt-1">
                            Size: {formatFileSize(selectedFile.size)}
                          </p>
                        </div>
                      </div>
                    ) : selectedFile.content !== undefined ? (
                      // Mermaid preview mode (.mmd / .mermaid files in preview)
                      isMermaidFile(selectedFile.name) && markdownViewMode === 'preview' ? (
                        <MermaidPreview source={editedContent ?? selectedFile.content} />
                      ) :
                      // Markdown preview mode
                      isMarkdownFile(selectedFile.name) && markdownViewMode === 'preview' ? (
                        <div className="h-full overflow-auto bg-base-100 p-6">
                          <article className="prose prose-invert prose-stone max-w-none
                            prose-headings:text-base-content prose-headings:font-semibold
                            prose-h1:text-2xl prose-h1:border-b prose-h1:border-base-300 prose-h1:pb-2
                            prose-h2:text-xl prose-h2:border-b prose-h2:border-base-300 prose-h2:pb-1
                            prose-p:text-base-content/90
                            prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-a:cursor-pointer
                            prose-strong:text-base-content prose-strong:font-semibold
                            prose-code:text-primary prose-code:bg-base-300 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                            prose-pre:bg-base-300 prose-pre:border prose-pre:border-base-content/10
                            prose-blockquote:border-l-primary prose-blockquote:text-base-content/70
                            prose-li:text-base-content/90
                            prose-table:border-collapse
                            prose-th:bg-base-300 prose-th:border prose-th:border-base-content/20 prose-th:px-3 prose-th:py-2
                            prose-td:border prose-td:border-base-content/20 prose-td:px-3 prose-td:py-2
                            prose-img:rounded-lg prose-img:shadow-md
                            prose-hr:border-base-300
                          ">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                a: ({ href, children, ...props }) => (
                                  <a
                                    {...props}
                                    href={href}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (href) handleMarkdownLinkClick(href);
                                    }}
                                  >
                                    {children}
                                  </a>
                                ),
                                // Render ```mermaid fenced blocks as actual
                                // diagrams instead of escaped source. Any
                                // other language falls through to the default
                                // ReactMarkdown <code> rendering so inline
                                // code + other-language fences look the same
                                // as before.
                                code: ({ className, children, ...props }) => {
                                  const lang = /language-(\w+)/.exec(className || '')?.[1];
                                  if (lang === 'mermaid') {
                                    const source = String(children).replace(/\n$/, '');
                                    return (
                                      <div className="not-prose my-4 border border-base-300 rounded-lg overflow-hidden bg-base-100">
                                        <div className="h-96">
                                          <MermaidPreview source={source} />
                                        </div>
                                      </div>
                                    );
                                  }
                                  return <code className={className} {...props}>{children}</code>;
                                },
                                // Checkbox support for task lists
                                input: ({ type, checked, ...props }) => {
                                  if (type === 'checkbox') {
                                    return (
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        readOnly
                                        className="checkbox checkbox-xs checkbox-primary mr-2 align-middle"
                                        {...props}
                                      />
                                    );
                                  }
                                  return <input type={type} {...props} />;
                                },
                              }}
                            >
                              {editedContent ?? selectedFile.content}
                            </ReactMarkdown>
                          </article>
                        </div>
                      ) : (
                        // Monaco editor for all other files and markdown edit
                        // mode. We deliberately do NOT key this on
                        // selectedFile.id — that forces a remount on every
                        // file click, which re-fires onMount → tsWorkspace
                        // re-init → another full eager load. With the URI
                        // collision fix in place, value-prop updates work
                        // cleanly, so a single Monaco instance is plenty.
                        <Editor
                          height="100%"
                          defaultLanguage={getMonacoLanguage(selectedFile.name)}
                          language={getMonacoLanguage(selectedFile.name)}
                          value={editedContent ?? selectedFile.content}
                          theme={monacoTheme}
                          onChange={handleEditorChange}
                          onMount={handleEditorMount}
                          options={{
                            readOnly: false,
                            minimap: { enabled: !isMobile },
                            lineNumbers: isMobile ? 'off' : 'on',
                            renderWhitespace: 'selection',
                            scrollBeyondLastLine: false,
                            fontSize: isMobile ? 12 : 13,
                            automaticLayout: true,
                            wordWrap: isMobile ? 'on' : 'off',
                            find: {
                              addExtraSpaceOnTop: false,
                              autoFindInSelection: 'never',
                              seedSearchStringFromSelection: 'always',
                            },
                          }}
                        />
                      )
                    ) : (
                      <div className="h-full flex items-center justify-center text-base-content/50">
                        <div className="text-center">
                          <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
                          <p>No preview available</p>
                          <p className="text-xs mt-2">File content could not be loaded</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="h-full flex items-center justify-center text-base-content/50">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p>Select a file to view</p>
                    <p className="text-xs mt-2">Press Ctrl/Cmd+P for quick search</p>
                  </div>
                </div>
              )}
            </div>
            </Panel>
            </>
          )}
        </PanelGroup>

        {/* Show editor button when collapsed */}
        {editorCollapsed && selectedFile && selectedFile.type === 'file' && (
          <button
            className="absolute top-4 right-4 btn btn-primary btn-sm gap-2"
            onClick={toggleEditor}
          >
            <Maximize2 className="w-4 h-4" />
            Show Editor
          </button>
        )}
      </div>

      {/* Quick file search modal */}
      {showQuickSearch && (
        <QuickFileSearch
          fileTree={fileTree}
          iconPack={iconPack}
          onSelect={async (node) => {
            // Check if node exists in the loaded tree
            const findInTree = (nodes: FileNodeWithContent[], path: string): FileNodeWithContent | null => {
              for (const n of nodes) {
                if (n.path.replace(/\\/g, '/') === path.replace(/\\/g, '/')) return n;
                if (n.children) { const f = findInTree(n.children, path); if (f) return f; }
              }
              return null;
            };
            const existing = findInTree(fileTree, node.path);
            if (existing) {
              handleSelect(existing);
            } else {
              // File from index — expand path first
              const expanded = await expandToPath(node.path);
              if (expanded) handleSelect(expanded);
            }
          }}
          onClose={() => setShowQuickSearch(false)}
        />
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-base-200 border border-base-300 rounded-lg shadow-xl py-1 min-w-[180px]"
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 200),
            top: Math.min(contextMenu.y, window.innerHeight - 200),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.node.type === 'file' && (
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors font-medium"
              onClick={() => { handleSelect(contextMenu.node); closeContextMenu(); }}
            >
              <Eye className="w-4 h-4 text-base-content/60" />
              View File
            </button>
          )}
          {contextMenu.node.type === 'file' && contextMenu.node.name.toLowerCase().endsWith('.md') && (
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
              onClick={exportAsPdf}
            >
              <FileOutput className="w-4 h-4 text-base-content/60" />
              Export as PDF
            </button>
          )}
          {contextMenu.node.type === 'folder' && (
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors font-medium"
              onClick={() => { handleToggle(contextMenu.node.id, contextMenu.node); closeContextMenu(); }}
            >
              <FolderOpen className="w-4 h-4 text-base-content/60" />
              {expandedIds.has(contextMenu.node.id) ? 'Collapse Folder' : 'Expand Folder'}
            </button>
          )}
          <div className="h-px bg-base-300 my-1" />
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
            onClick={copyPath}
          >
            <Copy className="w-4 h-4 text-base-content/60" />
            Copy Path
          </button>
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
            onClick={copyRelativePath}
          >
            <Copy className="w-4 h-4 text-base-content/60" />
            Copy Relative Path
          </button>
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
            onClick={copyName}
          >
            <FileTextIcon className="w-4 h-4 text-base-content/60" />
            Copy Name
          </button>
          <div className="h-px bg-base-300 my-1" />
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
            onClick={startRename}
          >
            <Pencil className="w-4 h-4 text-base-content/60" />
            Rename
          </button>
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 text-error transition-colors"
            onClick={deleteNode}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
          {contextMenu.node.type === 'folder' && (
            <>
              <div className="h-px bg-base-300 my-1" />
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
                onClick={() => startCreate('file')}
              >
                <FilePlus className="w-4 h-4 text-base-content/60" />
                New File
              </button>
              <button
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
                onClick={() => startCreate('folder')}
              >
                <FolderPlus className="w-4 h-4 text-base-content/60" />
                New Folder
              </button>
            </>
          )}
          <div className="h-px bg-base-300 my-1" />
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
            onClick={revealInExplorer}
          >
            <FolderSearch className="w-4 h-4 text-base-content/60" />
            Reveal in Explorer
          </button>
        </div>
      )}

      {/* Rename dialog */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRenameTarget(null)}>
          <div className="bg-base-100 rounded-lg shadow-xl w-96 p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">Rename "{renameTarget.name}"</h3>
            <input
              autoFocus
              type="text"
              className="input input-bordered w-full"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitRename();
                if (e.key === 'Escape') setRenameTarget(null);
              }}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn btn-sm btn-ghost" onClick={() => setRenameTarget(null)}>Cancel</button>
              <button className="btn btn-sm btn-primary" onClick={submitRename} disabled={!renameValue.trim()}>Rename</button>
            </div>
          </div>
        </div>
      )}

      {/* External-drop conflict resolution */}
      {conflictPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => {
            // Cancel = drop the queued sources too. User can retry the drop.
            dropQueueRef.current = [];
            setConflictPrompt(null);
          }}
        >
          <div
            className="bg-base-100 rounded-lg shadow-xl w-[28rem] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold mb-2">File already exists</h3>
            <p className="text-sm text-base-content/70 mb-4">
              <code className="text-amber-400">{conflictPrompt.fileName}</code> already exists in{' '}
              <code className="text-amber-400">
                {conflictPrompt.destFolderRel || '(project root)'}
              </code>
              . What should I do with the dropped file?
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  // Cancel this one AND drop the queued rest. Safer than
                  // implicitly applying a default to the queued items.
                  dropQueueRef.current = [];
                  setConflictPrompt(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={async () => {
                  if (!projectId) {
                    setConflictPrompt(null);
                    return;
                  }
                  const { sourcePath, destFolderRel } = conflictPrompt;
                  setConflictPrompt(null);
                  try {
                    await backend.files.copyExternalIntoProject(
                      projectId,
                      sourcePath,
                      destFolderRel,
                      'keepboth',
                    );
                  } catch (err) {
                    showToast(err instanceof Error ? err.message : 'Copy failed', 'error');
                  }
                  // Resume the queue.
                  void drainExternalDropQueue();
                }}
              >
                Keep both
              </button>
              <button
                className="btn btn-sm btn-error"
                onClick={async () => {
                  if (!projectId) {
                    setConflictPrompt(null);
                    return;
                  }
                  const { sourcePath, destFolderRel } = conflictPrompt;
                  setConflictPrompt(null);
                  try {
                    await backend.files.copyExternalIntoProject(
                      projectId,
                      sourcePath,
                      destFolderRel,
                      'overwrite',
                    );
                  } catch (err) {
                    showToast(err instanceof Error ? err.message : 'Copy failed', 'error');
                  }
                  void drainExternalDropQueue();
                }}
              >
                Overwrite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create file/folder dialog */}
      {createTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setCreateTarget(null)}>
          <div className="bg-base-100 rounded-lg shadow-xl w-96 p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">
              New {createTarget.type === 'file' ? 'File' : 'Folder'} in "{createTarget.parent.name}"
            </h3>
            <input
              autoFocus
              type="text"
              className="input input-bordered w-full"
              placeholder={createTarget.type === 'file' ? 'filename.ext' : 'folder-name'}
              value={createValue}
              onChange={(e) => setCreateValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitCreate();
                if (e.key === 'Escape') setCreateTarget(null);
              }}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn btn-sm btn-ghost" onClick={() => setCreateTarget(null)}>Cancel</button>
              <button className="btn btn-sm btn-primary" onClick={submitCreate} disabled={!createValue.trim()}>Create</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
